import { useRef, useState } from 'react';
import type { MouseEvent, ReactNode } from 'react';
import type { Post } from '../types';
import SafeIcon from './SafeIcon';
import RescheduleSheet from './RescheduleSheet';
import PlatformPreview from './PlatformPreview';
import RequestChangesDrawer from './RequestChangesDrawer';
import { CommentDialog } from './ReviewDialogs';
import { useApp, timeLabel } from '../i18n/AppContext';
import { useUi } from '../store/ui';
import { useDemo, useWorkspaceData } from '../store/DemoStore';
import { useViewTime } from '../store/time';
import { DEMO_TODAY, onAccount, aiWorks } from '../store/seed';
import { postStatusClassName, postStatusIconName } from './postStatusDisplay';
import { localizeDemoDatesInVariables } from './campaignDisplay';

const PDF_PAGE_COUNT = 6;
const CAROUSEL_SLIDE_COUNT = 3;
const sampleVideo = 'https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4';
const channelIcon: Record<string, string> = {
  Facebook: 'FiFacebook',
  Instagram: 'FiInstagram',
  TikTok: 'FiVideo',
  LinkedIn: 'FiLinkedin',
};

type OpenDialog = 'reject' | 'comment' | 'stop' | 'changes' | 'move' | null;

export default function PostView({
  post,
  reviewing,
  index,
  total,
  siblings,
  onOpen,
  onPrevious,
  onNext,
  onClose,
  onDecided,
  labels,
  waiting = 0,
  skipped = [],
}: {
  post?: Post;
  reviewing: boolean;
  index: number;
  total: number;
  siblings: Post[];
  waiting?: number;
  skipped?: string[];
  onOpen: (id: string) => void;
  onPrevious: () => void;
  onNext: () => void;
  onClose: () => void;
  onDecided: (id: string) => void;
  labels: Record<string, string>;
}) {
  const { translate, formatCount, readOnlyProps, language, readOnly } = useApp();
  const { actor, role, canApprove, canStop, notify, openChat, route, navigate, startReview } =
    useUi();
  const { commands } = useDemo();
  const {
    workspace,
    settings,
    accounts,
    campaigns,
    tasks,
    assets,
    posts: workspacePosts,
  } = useWorkspaceData(route.workspaceId);
  const viewTime = useViewTime();
  const [dialog, setDialog] = useState<OpenDialog>(null);
  const [previewMode, setPreviewMode] = useState<'post' | 'platform'>('post');
  const [undoMove, setUndoMove] = useState<{ day: number; time: string } | null>(null);
  const [version, setVersion] = useState<'Before' | 'After'>('After');
  const [slideIndex, setSlideIndex] = useState(0);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoMuted, setVideoMuted] = useState(true);
  const [captionsVisible, setCaptionsVisible] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  if (!post) {
    const skippedInThisQueue = reviewing ? skipped.length : 0;
    const otherPostsStillWaiting = reviewing
      ? workspacePosts.filter(
          (item) => item.status === 'Needs your review' && !skipped.includes(item.id)
        ).length
      : 0;
    const otherPostsStillWaitingMessage = () =>
      translate('{n} other posts in {ws} are still waiting for review.', {
        n: otherPostsStillWaiting,
        ws: workspace.name,
      });
    const finishedQueueBadge = () => {
      if (!reviewing) return translate('Post not found');
      if (otherPostsStillWaiting || skippedInThisQueue) return translate('This queue is done');
      return translate('All reviewed');
    };
    const finishedQueueHeading = () => {
      if (!reviewing) return translate('This post is no longer available');
      if (skippedInThisQueue) return translate('Almost done');
      if (otherPostsStillWaiting) return translate('This queue is done');
      return translate('All reviewed');
    };
    const finishedQueueMessage = () => {
      if (!reviewing) return translate('It may have been removed or replaced.');
      if (skippedInThisQueue)
        return (
          translate('You skipped {n} in this queue. They still need a decision.', {
            n: skippedInThisQueue,
          }) + (otherPostsStillWaiting ? ' ' + otherPostsStillWaitingMessage() : '')
        );
      if (otherPostsStillWaiting) return otherPostsStillWaitingMessage();
      return translate(
        'Nothing else needs your review right now. Approved posts are on the calendar.'
      );
    };
    return (
      <div className="content-stack">
        <div className="panel post-done">
          <span className="status published">
            <SafeIcon name="FiCheckCircle" />
            {finishedQueueBadge()}
          </span>
          <h1>{finishedQueueHeading()}</h1>
          <p className="subtitle">{finishedQueueMessage()}</p>
          <div className="button-row">
            {skippedInThisQueue > 0 && (
              <button className="button primary" onClick={() => onOpen(skipped[0])}>
                <SafeIcon name="FiRotateCcw" />
                {translate('Back to the {n} you skipped', { n: skippedInThisQueue })}
              </button>
            )}
            {otherPostsStillWaiting > 0 && (
              <button
                className={`button ${skippedInThisQueue ? '' : 'primary'}`}
                onClick={() => startReview()}
              >
                <SafeIcon name="FiEye" />
                {translate('Review the other {n}', { n: otherPostsStillWaiting })}
              </button>
            )}
            <button
              className={`button ${otherPostsStillWaiting || skippedInThisQueue ? '' : 'primary'}`}
              onClick={onClose}
            >
              <SafeIcon name="FiCalendar" />
              {translate('Back to calendar')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const needsReview = post.status === 'Needs your review';
  const revised = post.revision > 1 && !!post.previousCaption;
  const showBefore = revised && version === 'Before';
  const caption = showBefore ? post.previousCaption! : post.caption;
  const shownImage = showBefore && post.previousImage ? post.previousImage : post.image;
  const request = post.requestId ? tasks.find((task) => task.id === post.requestId) : undefined;
  const isVideo = post.format === 'Reel' || post.format === 'Story';
  const isCarousel = post.format === 'Carousel';
  const slideCount = isCarousel ? CAROUSEL_SLIDE_COUNT : 1;
  const otherLibraryPhotos = assets
    .filter(
      (asset) =>
        asset.kind === 'Photo' && asset.image && asset.image !== post.image && !asset.archived
    )
    .map((asset) => asset.image);
  const carouselSlideImages = isCarousel
    ? [post.image, ...otherLibraryPhotos.slice(0, CAROUSEL_SLIDE_COUNT - 1)]
    : [];
  const currentSlideImage =
    slideCount > 1 && slideIndex > 0 && carouselSlideImages[slideIndex]
      ? carouselSlideImages[slideIndex]
      : shownImage;
  const lastPdfPageIndex = PDF_PAGE_COUNT - 1;
  const when = new Intl.DateTimeFormat(language, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date(2026, 10, post.day));
  const formatShortDay = (dayOfMonth: number) =>
    new Intl.DateTimeFormat(language, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    }).format(new Date(2026, 10, dayOfMonth));
  const isPastDay = post.day < DEMO_TODAY;
  const mediaShapeClass = isVideo
    ? 'review-media-vertical'
    : post.channel === 'Instagram'
      ? 'review-media-portrait'
      : 'review-media-square';
  const publishedResultCount = post.results?.filter((result) => result.succeeded).length ?? 0;
  const disconnectedAccount = accounts.find(
    (account) => onAccount(post, account) && account.health !== 'Connected'
  );
  const hasNoProfileForPostYet = !accounts.some((account) => onAccount(post, account));
  const postCampaign = post.campaign
    ? campaigns.find((item) => item.key === post.campaign)
    : undefined;
  const isAwaitingPublishConfirmation =
    post.status === 'Published' &&
    post.results?.some((result) => result.uncertain) &&
    !post.results.some((result) => result.succeeded);
  const postingTimeNote = () => {
    if (!viewTime.viewerTimezoneDiffers) return '';
    const goesOutText = viewTime.sameDay(post.day, post.time)
      ? translate('goes out at {time} {zone}', { time: post.time, zone: viewTime.postingLabel })
      : translate('goes out {when} at {time} {zone}', {
          when,
          time: post.time,
          zone: viewTime.postingLabel,
        });
    return ` (${goesOutText})`;
  };
  const previousVersionsNewestFirst = [...(post.versions ?? [])].reverse();

  function approve() {
    if (post && hasNoProfileForPostYet) {
      notify(
        translate(
          'Connect a {channel} profile first. Then you can approve and schedule this post.',
          {
            channel: post.channel,
          }
        ),
        'warn'
      );
      return;
    }
    if (!post || !commands.approve(post.id, actor)) return;
    notify(
      translate('Approved. “{title}” is scheduled for {when} at {time}.', {
        title: post.title,
        when,
        time: post.time,
      })
    );
    onDecided(post.id);
  }
  function sendChanges(
    text: string,
    ids: string[],
    materials: { name: string; src?: string }[] = []
  ) {
    if (!post) return;
    const materialNames = materials.map((material) => material.name).join(', ');
    const requestWithFileNames = materials.length
      ? `${text} (${translate('with files: {names}', { names: materialNames })})`
      : text;
    const isUsablePhotoSource = (source?: string) =>
      !!source && !source.startsWith('data:image/svg');
    const replacementImageForNewVersion = materials
      .map(
        (material) =>
          assets.find((asset) => asset.name === material.name)?.image ??
          (isUsablePhotoSource(material.src) ? material.src : undefined)
      )
      .find(Boolean);
    ids.forEach((id) =>
      commands.requestChanges(id, requestWithFileNames, actor, replacementImageForNewVersion)
    );
    setDialog(null);
    notify(translate('Sent to AMY. The new version will appear here when it is ready.'));
    onDecided(post.id);
  }
  function reject(decision: 'replace' | 'empty' | 'later') {
    if (!post) return;
    commands.reject(post.id, decision, actor);
    setDialog(null);
    notify(rejectionMessage(decision));
    onDecided(post.id);
  }
  function rejectionMessage(decision: 'replace' | 'empty' | 'later') {
    if (decision === 'replace')
      return translate('Rejected. AMY is preparing a different idea for this slot.');
    if (decision === 'empty') return translate('Rejected. The slot stays empty.');
    return translate('Rejected. The slot waits for your decision.');
  }
  function stop() {
    if (!post) return;
    commands.stop(post.id, actor);
    setDialog(null);
    notify(translate('Stopped. It will not be published until someone resumes it.'));
  }
  function togglePlayback() {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) void video.play().catch(() => setVideoPlaying(false));
    else video.pause();
  }

  function toggleMuted() {
    setVideoMuted(!videoMuted);
    if (videoRef.current) videoRef.current.muted = !videoMuted;
  }
  function scrollToCaption(event: MouseEvent) {
    event.preventDefault();
    document.getElementById('post-caption')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  function undoMoveToPreviousSlot(previousSlot: { day: number; time: string }) {
    if (!post) return;
    const moveResult = commands.movePost(post.id, previousSlot.day, previousSlot.time, actor);
    setUndoMove(null);
    notify(
      moveResult === 'ok' ? translate('Move undone') : translate('The original slot is taken now.'),
      moveResult === 'ok' ? undefined : 'warn'
    );
  }
  function applySuggestedTime() {
    if (!post) return;
    const applyResult = commands.resolveTimeRequest(post.id, true, actor);
    notify(
      applyResult === 'ok'
        ? translate('New time applied.')
        : translate('That slot is taken now. Choose another time with Move.'),
      applyResult === 'ok' ? undefined : 'warn'
    );
  }
  function keepCurrentTime() {
    if (!post) return;
    commands.resolveTimeRequest(post.id, false, actor);
    notify(translate('Kept the current time.'));
  }
  function withdrawSuggestion() {
    if (!post) return;
    commands.resolveTimeRequest(post.id, false, actor);
    notify(translate('Suggestion withdrawn.'));
  }
  function resumeAtOriginalTime() {
    if (!post) return;
    const resumeResult = commands.resume(post.id, actor);
    notify(
      resumeResult === 'ok'
        ? translate('Resumed. It is scheduled again for {when} at {time}.', {
            when,
            time: post.time,
          })
        : translate(
            'The {channel} slot on {when} is now taken by another post. Ask AMY for a new time instead.',
            { channel: post.channel, when }
          )
    );
  }
  function saveComment(text: string) {
    if (!post) return;
    commands.comment(post.id, text, actor);
    setDialog(null);
    notify(translate('Comment saved'));
  }
  const closeDialog = () => setDialog(null);

  function renderApproveAction() {
    if (role === 'agency')
      return (
        <span className="status neutral action-note">
          <SafeIcon name="FiInfo" />
          {translate('{name} approves posts for {ws}', {
            name: workspace.owner,
            ws: workspace.name,
          })}
        </span>
      );
    if (!canApprove)
      return (
        <span className="status neutral action-note">
          <SafeIcon name="FiClock" />
          {translate('Awaiting approval from {name}', { name: workspace.owner })}
        </span>
      );
    return (
      <button className="button primary" onClick={approve} {...readOnlyProps}>
        <SafeIcon name="FiCheck" />
        {translate('Approve & schedule')}
      </button>
    );
  }

  return (
    <div className="content-stack post-view">
      <div className="page-title-row review-title-row">
        <div>
          <h1>{reviewing ? labels.reviewTitle : translate('Post details')}</h1>
          <div className="review-progress">
            {reviewing ? (
              translate('{i} of {n} · {posts} waiting for review', {
                i: index + 1,
                n: total,
                posts: formatCount(waiting, 'post'),
              })
            ) : (
              <>
                {workspace.name}
                {post.campaign && (
                  <>
                    {' '}
                    ·{' '}
                    {postCampaign ? (
                      <button
                        className="link-button"
                        onClick={() => navigate({ page: 'ideas', id: postCampaign.id })}
                      >
                        {postCampaign.name}
                      </button>
                    ) : (
                      post.campaign
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
        <button className="button" onClick={onClose}>
          <SafeIcon name="FiX" />
          {labels.close}
        </button>
      </div>
      <div className="mobile-review-header">
        <button className="button" onClick={onClose}>
          <SafeIcon name="FiX" />
          {labels.close}
        </button>
        <span className="review-progress">
          {reviewing ? `${index + 1} ${translate('of')} ${total}` : translate('Post details')}
        </span>
        {reviewing ? (
          <div className="review-header-nav">
            <button
              className="button"
              aria-label={labels.previous}
              disabled={index === 0}
              onClick={onPrevious}
            >
              <SafeIcon name="FiChevronLeft" />
            </button>
            <button
              className="button"
              aria-label={labels.next}
              disabled={index >= total - 1}
              onClick={onNext}
            >
              <SafeIcon name="FiChevronRight" />
            </button>
          </div>
        ) : (
          <span />
        )}
      </div>

      <div className="panel review-content-panel">
        <div className="panel-header">
          {reviewing ? (
            <div className="button-row review-mobile-controls">
              <button className="button small" disabled={index === 0} onClick={onPrevious}>
                <SafeIcon name="FiChevronLeft" />
                {labels.previous}
              </button>
              <button className="button small" disabled={index >= total - 1} onClick={onNext}>
                {labels.next}
                <SafeIcon name="FiChevronRight" />
              </button>
            </div>
          ) : (
            <span />
          )}
          {isAwaitingPublishConfirmation ? (
            <span className="status review">
              <SafeIcon name="FiHelpCircle" />
              {translate('Checking result')}
            </span>
          ) : (
            <span className={`status ${postStatusClassName(post.status)}`}>
              <SafeIcon name={postStatusIconName(post.status) as never} />
              {translate(post.needsNewTime ? 'Needs a new time' : post.status)}
            </span>
          )}
        </div>
        <div className="review-layout">
          <div className={`review-media-column ${mediaShapeClass}`}>
            <div className="segment preview-mode" role="group" aria-label={translate('Preview')}>
              <button
                className={previewMode === 'post' ? 'selected' : ''}
                aria-pressed={previewMode === 'post'}
                onClick={() => setPreviewMode('post')}
              >
                {translate('Post')}
              </button>
              <button
                className={previewMode === 'platform' ? 'selected' : ''}
                aria-pressed={previewMode === 'platform'}
                onClick={() => setPreviewMode('platform')}
              >
                {translate('As on {channel}', { channel: post.channel })}
              </button>
            </div>
            {previewMode === 'platform' && (
              <PlatformPreview
                post={post}
                image={shownImage}
                caption={caption}
                account={post.account}
              />
            )}
            <div
              className={`media-preview ${showBefore ? 'is-before' : ''}`}
              hidden={previewMode === 'platform'}
            >
              {isVideo ? (
                <>
                  <video
                    ref={videoRef}
                    className="review-video"
                    src={sampleVideo}
                    poster={shownImage}
                    playsInline
                    muted={videoMuted}
                    onPlay={() => setVideoPlaying(true)}
                    onPause={() => setVideoPlaying(false)}
                    aria-label={post.title}
                  />
                  <button
                    className={`review-video-play ${videoPlaying ? 'is-playing' : ''}`}
                    aria-label={videoPlaying ? translate('Pause video') : translate('Play video')}
                    onClick={togglePlayback}
                  >
                    <SafeIcon name={videoPlaying ? 'FiPause' : 'FiPlay'} size={30} />
                  </button>
                  <div className="review-video-controls">
                    <button
                      className="button small"
                      onClick={toggleMuted}
                      aria-label={videoMuted ? translate('Unmute video') : translate('Mute video')}
                      aria-pressed={!videoMuted}
                    >
                      <SafeIcon name={videoMuted ? 'FiVolumeX' : 'FiVolume2'} size={16} />
                    </button>
                    <button
                      className={`button small ${captionsVisible ? 'selected' : ''}`}
                      onClick={() => setCaptionsVisible((visible) => !visible)}
                      aria-label={translate('Toggle captions')}
                      aria-pressed={captionsVisible}
                    >
                      {translate('CC')}
                    </button>
                  </div>
                  {captionsVisible && (
                    <div className="review-video-caption" aria-live="polite">
                      {post.title}
                    </div>
                  )}
                </>
              ) : post.format === 'PDF' ? (
                <div className="doc-preview">
                  <SafeIcon name="FiFileText" size={40} />
                  <strong>{post.title}</strong>
                  <span className="muted">
                    {translate('Document · {n} pages', { n: PDF_PAGE_COUNT })}
                  </span>
                  <span className="tag">
                    {translate('Page {i} of {n}', { i: slideIndex + 1, n: PDF_PAGE_COUNT })}
                  </span>
                  <div className="button-row">
                    <button
                      className="button small"
                      disabled={slideIndex === 0}
                      onClick={() => setSlideIndex(slideIndex - 1)}
                      aria-label={translate('Previous page')}
                    >
                      <SafeIcon name="FiChevronLeft" />
                    </button>
                    <button
                      className="button small"
                      disabled={slideIndex === lastPdfPageIndex}
                      onClick={() => setSlideIndex(slideIndex + 1)}
                      aria-label={translate('Next page')}
                    >
                      <SafeIcon name="FiChevronRight" />
                    </button>
                  </div>
                </div>
              ) : (
                <img
                  src={currentSlideImage}
                  alt={
                    slideCount > 1
                      ? translate('{title}, slide {i} of {n}', {
                          title: post.title,
                          i: slideIndex + 1,
                          n: slideCount,
                        })
                      : post.title
                  }
                />
              )}
              {slideCount > 1 && (
                <div className="carousel-nav">
                  <button
                    className="button small"
                    disabled={slideIndex === 0}
                    onClick={() => setSlideIndex(slideIndex - 1)}
                    aria-label={translate('Previous slide')}
                  >
                    <SafeIcon name="FiChevronLeft" />
                  </button>
                  <span>
                    {slideIndex + 1} / {slideCount}
                  </span>
                  <button
                    className="button small"
                    disabled={slideIndex === slideCount - 1}
                    onClick={() => setSlideIndex(slideIndex + 1)}
                    aria-label={translate('Next slide')}
                  >
                    <SafeIcon name="FiChevronRight" />
                  </button>
                </div>
              )}
              {post.format !== 'PDF' && (
                <span className="media-caption">
                  {translate(post.format)}
                  {isVideo ? ' · 0:24' : ''}
                </span>
              )}
              {revised && (
                <div className="mobile-media-version" aria-label={translate('Choose version')}>
                  <button
                    className={`button ${version === 'Before' ? 'selected' : ''}`}
                    aria-pressed={version === 'Before'}
                    onClick={() => setVersion('Before')}
                  >
                    {translate('Before')}
                  </button>
                  <button
                    className={`button ${version === 'After' ? 'selected' : ''}`}
                    aria-pressed={version === 'After'}
                    onClick={() => setVersion('After')}
                  >
                    {translate('After')}
                  </button>
                </div>
              )}
            </div>
            <a className="read-caption-link" href="#post-caption" onClick={scrollToCaption}>
              <SafeIcon name="FiArrowDown" size={14} />
              {translate('Read the caption')}
            </a>
          </div>

          <div className="content-stack">
            {siblings.length > 1 && (
              <div
                className="version-tabs"
                role="tablist"
                aria-label={translate('Versions of this post')}
              >
                {siblings.map((item) => (
                  <button
                    key={item.id}
                    role="tab"
                    aria-selected={item.id === post.id}
                    className={`channel-chip ${item.id === post.id ? 'selected' : ''}`}
                    onClick={() => item.id !== post.id && onOpen(item.id)}
                  >
                    <SafeIcon name={channelIcon[item.channel] as never} size={14} />
                    {item.channel}
                    <span
                      className={`version-dot ${postStatusClassName(item.status)}`}
                      title={translate(item.status)}
                    />
                  </button>
                ))}
              </div>
            )}
            <div className="review-post-summary">
              <h3>{post.title}</h3>
              <div className="subtitle">
                {post.channel} · {post.account || translate('no profile yet')} ·{' '}
                {viewTime.viewerDateTime(post.day, post.time)} · {viewTime.viewerTimezoneLabel}
                {postingTimeNote()}
              </div>
              <div className="muted post-meta-line">
                {translate('Content language: {lang}', {
                  lang: post.language === 'pl' ? 'Polski' : 'English',
                })}
                {post.revision > 1 ? ` · ${translate('Version {n}', { n: post.revision })}` : ''}
              </div>
            </div>

            {hasNoProfileForPostYet && post.status === 'Needs your review' && (
              <div
                className="notice-banner status-panel-inline warn connection-warning"
                role="status"
              >
                <SafeIcon name="FiLink" />
                <div>
                  <strong>
                    {translate('No {channel} profile connected yet', { channel: post.channel })}
                  </strong>
                  <div>
                    {translate(
                      'You can review and change this post now. To approve and schedule it, connect a profile first.'
                    )}
                  </div>
                </div>
                <button
                  className="button small primary"
                  onClick={() => navigate({ page: 'settings', id: 'connections' })}
                >
                  {translate('Connect a profile')}
                </button>
              </div>
            )}
            {disconnectedAccount && ['Scheduled', 'Needs your review'].includes(post.status) && (
              <div
                className="notice-banner status-panel-inline warn connection-warning"
                role="status"
              >
                <SafeIcon name="FiAlertTriangle" />
                <div>
                  <strong>
                    {translate('{network} needs reconnecting before this can be published', {
                      network: post.channel,
                    })}
                  </strong>
                  <div>
                    {post.status === 'Scheduled'
                      ? translate(
                          'The approval is kept. Reconnect {account} and the post goes out as planned; if the time passes first, AMY asks for a new time.',
                          { account: disconnectedAccount.name }
                        )
                      : translate(
                          'You can still approve it. It will only be published once {account} is reconnected.',
                          { account: disconnectedAccount.name }
                        )}
                  </div>
                </div>
                {role === 'owner' && !readOnly ? (
                  <button
                    className="button small primary"
                    onClick={() => {
                      commands.reconnect(disconnectedAccount.id);
                      notify(translate('Profile reconnected (simulated)'));
                    }}
                  >
                    {translate('Reconnect {network}', { network: post.channel })}
                  </button>
                ) : (
                  <span className="muted small-print">
                    {translate('{name} can reconnect it in Settings', {
                      name: disconnectedAccount.owner,
                    })}
                  </span>
                )}
              </div>
            )}
            {undoMove && (
              <div className="notice-banner calendar-undo">
                <SafeIcon name="FiCalendar" />
                <span>
                  {translate('Moved. It was on {when} at {time}.', {
                    when: formatShortDay(undoMove.day),
                    time: undoMove.time,
                  })}
                </span>
                <button className="button small" onClick={() => undoMoveToPreviousSlot(undoMove)}>
                  {translate('Undo')}
                </button>
              </div>
            )}
            {post.timeRequest && (
              <div className="notice-banner time-request">
                <SafeIcon name="FiCalendar" />
                <div>
                  <strong>
                    {translate('{name} suggests {when} at {time}', {
                      name: post.timeRequest.by,
                      when: formatShortDay(post.timeRequest.day),
                      time: post.timeRequest.time,
                    })}
                  </strong>
                  {post.timeRequest.note && <div className="muted">“{post.timeRequest.note}”</div>}
                  <div className="muted small-print">
                    {translate(
                      role === 'agency'
                        ? 'Apply it, or keep the current time.'
                        : 'Waiting for Hakusana to confirm. The post keeps its current time until then.'
                    )}
                  </div>
                </div>
                {role === 'agency' ? (
                  <span className="button-row">
                    <button className="button small primary" onClick={applySuggestedTime}>
                      {translate('Apply')}
                    </button>
                    <button className="button small" onClick={keepCurrentTime}>
                      {translate('Keep current time')}
                    </button>
                  </span>
                ) : (
                  <button
                    className="button small ghost"
                    onClick={withdrawSuggestion}
                    {...readOnlyProps}
                  >
                    {translate('Withdraw')}
                  </button>
                )}
              </div>
            )}
            <StatusPanel
              post={post}
              past={isPastDay}
              paused={settings.paused}
              okResults={publishedResultCount}
            />

            <section
              className="panel review-caption"
              id="post-caption"
              aria-labelledby="caption-heading"
            >
              <h3 id="caption-heading">
                {translate('Caption')}
                {showBefore && <span className="tag">{translate('Before')}</span>}
              </h3>
              <p className="caption-box">{caption}</p>
            </section>
            {request && (
              <section
                className="notice-banner post-request"
                aria-label={translate('Where this post came from')}
              >
                <SafeIcon name="FiZap" />
                <div>
                  <strong>{translate('From {name}’s request', { name: request.by })}</strong>
                  <div>
                    “
                    {request.showAsWritten
                      ? request.title
                      : translate(request.title, request.variables)}
                    ”
                  </div>
                  {request.materials?.length ? (
                    <div className="chat-attachments">
                      {request.materials.map((materialName) => (
                        <span key={materialName} className="attachment-chip">
                          <SafeIcon name="FiPaperclip" size={12} />
                          {materialName}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  <button
                    className="link-button"
                    onClick={() => navigate({ page: 'ideas', id: 'tasks' })}
                  >
                    {translate('See the task')}
                  </button>
                </div>
              </section>
            )}
            {revised && (
              <section className="notice-banner review-changes" aria-labelledby="changes-heading">
                <div>
                  <strong id="changes-heading">
                    {translate('What changed in version {n}', { n: post.revision })}
                  </strong>
                  <div className="muted">{translate(post.changeNote ?? '')}</div>
                  <div
                    className="button-row review-version-buttons"
                    aria-label={translate('Choose version')}
                  >
                    <button
                      className={`button small ${version === 'Before' ? 'primary' : ''}`}
                      aria-pressed={version === 'Before'}
                      onClick={() => setVersion('Before')}
                    >
                      {translate('Before')}
                    </button>
                    <button
                      className={`button small ${version === 'After' ? 'primary' : ''}`}
                      aria-pressed={version === 'After'}
                      onClick={() => setVersion('After')}
                    >
                      {translate('After')}
                    </button>
                  </div>
                </div>
              </section>
            )}
            {(post.versions?.length ?? 0) > 0 && (
              <details className="post-activity version-history">
                <summary>
                  <SafeIcon name="FiLayers" size={14} />{' '}
                  {translate('Version history ({n})', { n: (post.versions?.length ?? 0) + 1 })}
                </summary>
                <div className="version-row current">
                  <img src={post.image} alt="" />
                  <div>
                    <strong>{translate('Version {n} · current', { n: post.revision })}</strong>
                    <p>{post.caption}</p>
                    {post.changeNote && (
                      <span className="muted small-print">
                        {translate('Change: {note}', { note: post.changeNote })}
                      </span>
                    )}
                  </div>
                </div>
                {previousVersionsNewestFirst.map((previousVersion) => (
                  <div key={previousVersion.revision} className="version-row">
                    <img src={previousVersion.image} alt="" />
                    <div>
                      <strong>{translate('Version {n}', { n: previousVersion.revision })}</strong>
                      <p>{previousVersion.caption}</p>
                      {previousVersion.image !== post.image && (
                        <span className="muted small-print">{translate('Different photo')}</span>
                      )}
                    </div>
                  </div>
                ))}
              </details>
            )}
            {post.comments.length > 0 && (
              <div className="panel post-comments">
                <h3>{translate('Comments')}</h3>
                {post.comments.map((item, commentIndex) => (
                  <p key={commentIndex}>
                    <strong>{item.by}</strong> <span className="muted">· {item.at}</span>
                    <br />
                    {item.text}
                  </p>
                ))}
              </div>
            )}
            <details className="post-activity">
              <summary>
                <SafeIcon name="FiClock" size={14} /> {translate('Activity')}
              </summary>
              {[...post.history].reverse().map((item, historyIndex) => (
                <div key={historyIndex} className="activity-row">
                  <span className="muted">{timeLabel(item.at, language, translate)}</span>
                  <span>
                    <strong>{item.by}</strong> ·{' '}
                    {item.showAsWritten
                      ? item.text
                      : translate(
                          item.text,
                          localizeDemoDatesInVariables(item.variables, language)
                        )}
                  </span>
                </div>
              ))}
            </details>
          </div>
        </div>
      </div>

      <div className="review-actions">
        {needsReview && (
          <>
            {renderApproveAction()}
            <button
              className="button"
              id="review-request-changes"
              onClick={() => setDialog('changes')}
              {...readOnlyProps}
            >
              <SafeIcon name="FiEdit3" />
              {translate('Request changes')}
            </button>
            {reviewing && (
              <button className="button ghost" onClick={onNext}>
                <SafeIcon name="FiSkipForward" />
                {translate('Skip for now')}
              </button>
            )}
            <MoreMenu
              label={labels.more}
              items={[
                {
                  icon: 'FiXCircle',
                  label: translate('Reject idea'),
                  danger: true,
                  onClick: () => setDialog('reject'),
                  disabled: role === 'agency' || readOnly,
                },
                {
                  icon: 'FiMessageSquare',
                  label: translate('Comment'),
                  onClick: () => setDialog('comment'),
                  disabled: readOnly,
                },
                {
                  icon: 'FiCalendar',
                  label: translate(
                    role === 'agency' ? 'Move to another day' : 'Suggest a new time'
                  ),
                  onClick: () => setDialog('move'),
                  disabled: readOnly || !!post.timeRequest,
                },
              ]}
            />
          </>
        )}
        {post.status === 'Scheduled' && (
          <>
            <label className="brand-check compact no-late">
              <input
                type="checkbox"
                checked={!!post.neverPublishLate}
                onChange={(event) => commands.setNoLate(post.id, event.target.checked)}
                disabled={readOnly}
              />{' '}
              {translate('Do not publish late')}
            </label>
            {canStop ? (
              <button
                className="button danger-outline"
                onClick={() => setDialog('stop')}
                {...readOnlyProps}
              >
                <SafeIcon name="FiStopCircle" />
                {translate('Stop this post')}
              </button>
            ) : (
              <span className="status neutral action-note">
                <SafeIcon name="FiInfo" />
                {translate('Only {name} can stop scheduled posts', { name: workspace.owner })}
              </span>
            )}
            <button
              className="button"
              onClick={() => setDialog('move')}
              disabled={readOnly || !!post.timeRequest}
              title={readOnlyProps.title}
            >
              <SafeIcon name="FiCalendar" />
              {translate(role === 'agency' ? 'Move' : 'Suggest a new time')}
            </button>
            <button
              className="button"
              id="review-request-changes"
              onClick={() => setDialog('changes')}
              {...readOnlyProps}
            >
              <SafeIcon name="FiEdit3" />
              {translate('Request a revision')}
            </button>
            <button className="button" onClick={() => setDialog('comment')} {...readOnlyProps}>
              <SafeIcon name="FiMessageSquare" />
              {translate('Comment')}
            </button>
          </>
        )}
        {post.status === 'Approved · Stopped' && (
          <>
            {canStop && !isPastDay && !post.needsNewTime && (
              <button className="button primary" onClick={resumeAtOriginalTime} {...readOnlyProps}>
                <SafeIcon name="FiPlayCircle" />
                {translate('Resume at the original time')}
              </button>
            )}
            <button
              className={`button ${isPastDay || post.needsNewTime ? 'primary' : ''}`}
              onClick={() => setDialog('move')}
              disabled={readOnly || !!post.timeRequest}
              title={readOnlyProps.title}
            >
              <SafeIcon name="FiCalendar" />
              {translate(role === 'agency' ? 'Choose a new time' : 'Suggest a new time')}
            </button>
            <button
              className="button"
              onClick={() =>
                openChat(
                  translate('Please find a new time for “{title}” ({channel}).', {
                    title: post.title,
                    channel: post.channel,
                  })
                )
              }
              {...readOnlyProps}
            >
              <SafeIcon name="FiMessageCircle" />
              {translate('Ask AMY for a new time')}
            </button>
            <button className="button" onClick={() => setDialog('comment')} {...readOnlyProps}>
              <SafeIcon name="FiMessageSquare" />
              {translate('Comment')}
            </button>
          </>
        )}
        {post.status === 'Planned' && (
          <>
            <button
              className="button"
              onClick={() =>
                openChat(translate('About “{title}” on {when}: ', { title: post.title, when }))
              }
              {...readOnlyProps}
            >
              <SafeIcon name="FiMessageCircle" />
              {translate('Tell AMY something about this post')}
            </button>
            <button
              className="button"
              onClick={() => setDialog('move')}
              disabled={readOnly || !!post.timeRequest}
              title={readOnlyProps.title}
            >
              <SafeIcon name="FiCalendar" />
              {translate(role === 'agency' ? 'Move' : 'Suggest a new time')}
            </button>
          </>
        )}
        {post.status === 'Needs your input' && (
          <>
            <button
              className="button primary"
              onClick={() => navigate({ page: 'materials' })}
              {...readOnlyProps}
            >
              <SafeIcon name="FiUpload" />
              {translate('Add the material')}
            </button>
            <button
              className="button"
              onClick={() =>
                openChat(
                  translate('For “{title}”, please use photos instead of video.', {
                    title: post.title,
                  })
                )
              }
              {...readOnlyProps}
            >
              {translate('Use photos instead')}
            </button>
          </>
        )}
        {(post.status === 'Intentionally empty' || post.status === 'Rejected') && (
          <button
            className="button"
            onClick={() =>
              openChat(
                translate('Please suggest a new idea for {channel} on {when}.', {
                  channel: post.channel,
                  when,
                })
              )
            }
            {...readOnlyProps}
          >
            <SafeIcon name="FiZap" />
            {translate('Ask AMY for an idea')}
          </button>
        )}
        {(post.status === 'Published' || post.status === 'AMY is working') && (
          <button className="button" onClick={() => setDialog('comment')} {...readOnlyProps}>
            <SafeIcon name="FiMessageSquare" />
            {translate('Comment')}
          </button>
        )}
      </div>

      {dialog === 'changes' && (
        <RequestChangesDrawer
          post={post}
          siblings={siblings}
          onClose={closeDialog}
          onSend={sendChanges}
        />
      )}
      {dialog === 'move' && (
        <RescheduleSheet
          post={post}
          onClose={closeDialog}
          onMoved={(previousSlot) => setUndoMove(previousSlot)}
        />
      )}
      {dialog === 'comment' && <CommentDialog onCancel={closeDialog} onSave={saveComment} />}
      {dialog === 'reject' && (
        <Modal title={translate('Reject idea')} onClose={closeDialog}>
          <p>
            {translate(
              'This idea has been rejected. Would you like AMY to prepare a different idea for this slot?'
            )}
          </p>
          <div className="button-row">
            <button className="button primary" onClick={() => reject('replace')}>
              {translate('Yes, create a new idea')}
            </button>
            <button className="button" onClick={() => reject('empty')}>
              {translate('No, leave the slot empty')}
            </button>
            <button className="button ghost" onClick={() => reject('later')}>
              {translate('Decide later')}
            </button>
          </div>
        </Modal>
      )}
      {dialog === 'stop' && (
        <Modal title={translate('Stop this post?')} onClose={closeDialog}>
          <p>
            {translate(
              '“{title}” will not be published on {when} at {time}. It stays on hold until someone resumes it or asks for a new time.',
              { title: post.title, when, time: post.time }
            )}
          </p>
          {siblings.length > 1 && (
            <p className="muted">
              {translate(
                'Only the {channel} version stops. Other versions keep their current status.',
                {
                  channel: post.channel,
                }
              )}
            </p>
          )}
          <div className="button-row">
            <button className="button danger" onClick={stop}>
              <SafeIcon name="FiStopCircle" />
              {translate('Stop post')}
            </button>
            <button className="button" onClick={closeDialog}>
              {translate('Keep it scheduled')}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function StatusPanel({
  post,
  past,
  paused,
  okResults,
}: {
  post: Post;
  past: boolean;
  paused: boolean;
  okResults: number;
}) {
  const { translate, readOnlyProps, readOnly } = useApp();
  const { actor, notify, route } = useUi();
  const { commands, state } = useDemo();
  const accountFor = (resultAccountLabel: string) =>
    state.accounts.find(
      (account) =>
        account.workspaceId === route.workspaceId &&
        account.name === resultAccountLabel.replace(/ \((company page|personal)\)$/, '')
    );
  const inviteSentAt = (accountId: string) => state.reminders[`connect-${accountId}`];
  const viewTime = useViewTime();
  const aiIsUnavailable = !aiWorks(
    state.aiConnections.find((aiConnection) => aiConnection.workspaceId === route.workspaceId)
  );
  if (post.status === 'Published' && post.results) {
    const results = post.results;
    type PublishResult = (typeof results)[number];
    const isDefinitelyFailed = (result: PublishResult) => !result.succeeded && !result.uncertain;
    const accountIsConnected = (result: PublishResult) =>
      accountFor(result.account)?.health === 'Connected';
    const publishSummaryHeading =
      results.some((result) => result.uncertain) && okResults === 0
        ? translate('Waiting for confirmation')
        : translate('Published on {ok} of {n}', { ok: okResults, n: results.length });
    const publishSummaryAdvice = () => {
      if (results.every((result) => result.succeeded))
        return translate('Nothing is needed from you.');
      if (results.some((result) => isDefinitelyFailed(result) && !accountIsConnected(result)))
        return translate(
          'One account needs its owner to connect it. The others are done and will not be posted again.'
        );
      if (results.some(isDefinitelyFailed))
        return translate(
          'The account is connected now. Publish it there; the others will not be posted again.'
        );
      return translate('AMY is confirming one result. You do not need to do anything.');
    };
    const resultIconName = (result: PublishResult) => {
      if (result.succeeded) return 'FiCheckCircle';
      if (result.uncertain) return 'FiHelpCircle';
      return 'FiAlertTriangle';
    };
    const resultDescription = (result: PublishResult) => {
      if (result.succeeded)
        return translate('Published at {time} · demo result, no real post', {
          time: `${viewTime.viewerTime(post.day, result.at ?? post.time)} ${viewTime.viewerTimezoneLabel}`,
        });
      if (!result.uncertain && accountIsConnected(result))
        return translate('Connected now. You can publish to this account.');
      return translate(result.reason ?? 'Not published');
    };
    const checkResult = (result: PublishResult) => {
      commands.checkPublishResult(post.id, result.account, actor);
      notify(translate('Checked: it was published. Nothing was posted twice.'));
    };
    const retryPublishing = (result: PublishResult) => {
      const retryResult = commands.retryPublish(post.id, result.account, actor);
      notify(
        retryResult === 'ok'
          ? translate('Published to {account}.', { account: result.account })
          : translate('{account} is still not connected.', {
              account: result.account,
            }),
        retryResult === 'ok' ? undefined : 'warn'
      );
    };
    const renderFailedResultActions = (result: PublishResult) => {
      const account = accountFor(result.account);
      if (account?.health === 'Connected')
        return (
          <button
            className="button small primary"
            disabled={readOnly}
            onClick={() => retryPublishing(result)}
          >
            <SafeIcon name="FiRotateCw" size={14} />
            {translate('Publish now')}
          </button>
        );
      return (
        <span className="button-row">
          {account &&
            (inviteSentAt(account.id) ? (
              <span className="muted small-print">
                {translate('Invite sent at {time}', { time: inviteSentAt(account.id) })}
              </span>
            ) : (
              <button
                className="button small"
                {...readOnlyProps}
                onClick={() => {
                  commands.remind(`connect-${account.id}`);
                  notify(
                    translate('Invite sent to {name} (simulated)', {
                      name: account.owner,
                    })
                  );
                }}
              >
                {translate('Invite to connect')}
              </button>
            ))}
          {account && (
            <button
              className="button small ghost"
              title={translate('Demo only')}
              onClick={() => {
                commands.reconnect(account.id);
                notify(
                  translate('{name} connected the account (simulated). You can publish now.', {
                    name: account.owner,
                  })
                );
              }}
            >
              {translate('Simulate: connected')}
            </button>
          )}
        </span>
      );
    };
    return (
      <div className="panel status-panel">
        <h3>
          <SafeIcon name="FiSend" size={15} /> {publishSummaryHeading}
        </h3>
        <p className="muted small-print">{publishSummaryAdvice()}</p>
        {results.map((result) => (
          <div className="result-row" key={result.account}>
            <SafeIcon name={resultIconName(result)} className={result.succeeded ? 'ok' : 'warn'} />
            <div>
              <strong>{result.account}</strong>
              <div className="muted">{resultDescription(result)}</div>
              {result.succeeded && (
                <span className="muted small-print">
                  <SafeIcon name="FiExternalLink" size={12} />{' '}
                  {translate('Open published post: not available in the demo')}
                </span>
              )}
            </div>
            {!result.succeeded && result.uncertain && (
              <button
                className="button small"
                disabled={readOnly}
                onClick={() => checkResult(result)}
              >
                <SafeIcon name="FiSearch" size={14} />
                {translate('Check result')}
              </button>
            )}
            {isDefinitelyFailed(result) && renderFailedResultActions(result)}
          </div>
        ))}
        {okResults > 0 && (
          <p className="muted small-print">
            {translate('Reach after 24 hours (simulated): {n}', { n: '1 240' })}
          </p>
        )}
      </div>
    );
  }
  if (post.status === 'Scheduled')
    return (
      <div className={`notice-banner status-panel-inline ${paused ? 'warn' : ''}`}>
        <SafeIcon name={paused ? 'FiPauseCircle' : 'FiClock'} />
        <span>
          {paused
            ? translate('Publishing is paused. This post waits until publishing is resumed.')
            : translate(
                'Scheduled. You can still stop it or ask for a revision before it goes out.'
              )}
        </span>
      </div>
    );
  if (post.status === 'Approved · Stopped' && post.needsNewTime)
    return (
      <div className="notice-banner status-panel-inline warn">
        <SafeIcon name="FiAlertTriangle" />
        <div>
          <strong>{translate('Needs a new time')}</strong>
          <div>
            {translate(
              'Its time passed and it was not published. Choose or suggest a new time; nothing is rebooked automatically.'
            )}
          </div>
        </div>
      </div>
    );
  if (post.status === 'Approved · Stopped')
    return (
      <div className="notice-banner status-panel-inline warn">
        <SafeIcon name="FiPauseCircle" />
        <span>
          {past
            ? translate(
                'Stopped. Its time has passed, so ask AMY for a new time if you still want it.'
              )
            : translate('Stopped and on hold. It will not be published until someone resumes it.')}
        </span>
      </div>
    );
  if (post.status === 'AMY is working' && aiIsUnavailable)
    return (
      <div className="notice-banner warn status-panel-inline">
        <SafeIcon name="FiPauseCircle" />
        <div>
          <strong>{translate('Waiting for AI')}</strong>
          <div>
            {translate(
              'The AI key was removed, so AMY cannot make version {n} yet. Your request is saved and continues as soon as AI is connected again.',
              { n: post.revision + 1 }
            )}
          </div>
        </div>
      </div>
    );
  if (post.status === 'AMY is working')
    return (
      <div className="panel status-panel">
        <div className="progress-line">
          <SafeIcon name="FiLoader" />
          <strong>{translate('AMY is preparing version {n}…', { n: post.revision + 1 })}</strong>
        </div>
        <div className="progress-track">
          <span className="progress-indeterminate" />
        </div>
        <p className="muted small-print">
          {translate('You will get a notification when it is ready to review.')}
        </p>
      </div>
    );
  if (post.status === 'Planned')
    return (
      <div className="notice-banner status-panel-inline">
        <SafeIcon name="FiEdit" />
        <span>
          {translate('Planned. AMY writes the first draft about three days before it is due.')}
        </span>
      </div>
    );
  if (post.status === 'Needs your input')
    return (
      <div className="notice-banner status-panel-inline warn">
        <SafeIcon name="FiHelpCircle" />
        <div>
          <strong>{translate('AMY needs material for this post')}</strong>
          <div>{translate(post.missing ?? '')}</div>
        </div>
      </div>
    );
  if (post.status === 'Intentionally empty')
    return (
      <div className="notice-banner status-panel-inline">
        <SafeIcon name="FiMinusCircle" />
        <span>{translate('This slot was left empty on purpose. Nothing will be published.')}</span>
      </div>
    );
  if (post.status === 'Cancelled')
    return (
      <div className="notice-banner status-panel-inline">
        <SafeIcon name="FiXCircle" />
        <span>{translate('Cancelled when its campaign ended. It will not be published.')}</span>
      </div>
    );
  if (post.status === 'Rejected')
    return (
      <div className="notice-banner status-panel-inline warn">
        <SafeIcon name="FiXCircle" />
        <span>{translate('This idea was rejected and will not be published.')}</span>
      </div>
    );
  return null;
}

function MoreMenu({
  label,
  items,
}: {
  label: string;
  items: {
    icon: string;
    label: string;
    onClick: () => void;
    danger?: boolean;
    disabled?: boolean;
  }[];
}) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="more-menu">
      <button
        className="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
      >
        <SafeIcon name="FiMoreHorizontal" />
        {label}
      </button>
      {isOpen && (
        <div role="menu" className="more-menu-list">
          {items.map((item) => (
            <button
              key={item.label}
              role="menuitem"
              className={`button ${item.danger ? 'danger' : ''}`}
              disabled={item.disabled}
              onClick={() => {
                setIsOpen(false);
                item.onClick();
              }}
            >
              <SafeIcon name={item.icon as never} />
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section className="panel modal-card" role="dialog" aria-modal="true" aria-label={title}>
        <h2>{title}</h2>
        {children}
      </section>
    </div>
  );
}
